import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  InputAdornment,
  TextField,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSearch } from '@/contexts/SearchContext';
import { PropertySearchParams } from '@/contexts/SearchContext';
import SearchModal from './SearchModal';

interface SearchBarProps {
  onSearch?: (params: PropertySearchParams) => void;
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'hero' | 'compact';
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  placeholder = "Where to?",
  size = 'large',
  variant = 'hero',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const { state, setSearchModalOpen, addRecentSearch } = useSearch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get current search params for display
  const currentParams = state.searchParams;
  const hasSearchParams = Boolean(
    currentParams.location || 
    currentParams.checkIn || 
    currentParams.checkOut || 
    currentParams.guests
  );

  // Handle opening the search modal
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setSearchModalOpen(true);
  };

  // Handle closing the search modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSearchModalOpen(false);
  };

  // Handle search submission
  const handleSearch = (params: PropertySearchParams) => {
    addRecentSearch(params);
    if (onSearch) {
      onSearch(params);
    }
  };

  // Format display text for search parameters
  const getLocationText = () => {
    return currentParams.location || placeholder;
  };

  const getDateText = () => {
    if (currentParams.checkIn && currentParams.checkOut) {
      const checkIn = new Date(currentParams.checkIn);
      const checkOut = new Date(currentParams.checkOut);
      return `${format(checkIn, 'MMM d')} - ${format(checkOut, 'MMM d')}`;
    }
    return 'Add dates';
  };

  const getGuestText = () => {
    const guests = currentParams.guests || 0;
    if (guests === 0) return 'Add guests';
    return guests === 1 ? '1 guest' : `${guests} guests`;
  };

  // Responsive sizing
  const getSearchBarHeight = () => {
    if (size === 'small') return 56;
    if (size === 'medium') return 64;
    return isMobile ? 64 : 72;
  };

  const getSearchBarPadding = () => {
    if (size === 'small') return 1;
    if (size === 'medium') return 1.5;
    return isMobile ? 1.5 : 2;
  };

  // Hero variant (large, prominent search bar)
  if (variant === 'hero') {
    return (
      <>
        <Paper
          elevation={2}
          onClick={handleOpenModal}
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: getSearchBarHeight(),
            borderRadius: isMobile ? 4 : 6,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            border: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            maxWidth: isMobile ? '100%' : 850,
            mx: 'auto',
            overflow: 'hidden',
          }}
        >
          {isMobile ? (
            // Mobile: Single row with icon and text
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                px: getSearchBarPadding(),
                minHeight: 56, // Ensure touch-friendly height
              }}
            >
              <SearchIcon sx={{ color: 'text.secondary', mr: 2, fontSize: '1.5rem' }} />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  fontWeight={hasSearchParams ? 600 : 400}
                  color={hasSearchParams ? 'text.primary' : 'text.secondary'}
                  sx={{ 
                    lineHeight: 1.2,
                    fontSize: '1.1rem',
                  }}
                >
                  {hasSearchParams ? getLocationText() : placeholder}
                </Typography>
                {hasSearchParams && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: '0.9rem' }}
                  >
                    {getDateText()} • {getGuestText()}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            // Desktop: Segmented search inputs
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {/* Where */}
              <Box
                sx={{
                  flex: 1,
                  px: getSearchBarPadding(),
                  py: 1,
                  borderRight: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Where
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={currentParams.location ? 500 : 400}
                  color={currentParams.location ? 'text.primary' : 'text.secondary'}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getLocationText()}
                </Typography>
              </Box>

              {/* Check-in */}
              <Box
                sx={{
                  flex: 0.8,
                  px: getSearchBarPadding(),
                  py: 1,
                  borderRight: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Check in
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={currentParams.checkIn ? 500 : 400}
                  color={currentParams.checkIn ? 'text.primary' : 'text.secondary'}
                >
                  {currentParams.checkIn ? format(new Date(currentParams.checkIn), 'MMM d') : 'Add dates'}
                </Typography>
              </Box>

              {/* Check-out */}
              <Box
                sx={{
                  flex: 0.8,
                  px: getSearchBarPadding(),
                  py: 1,
                  borderRight: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Check out
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={currentParams.checkOut ? 500 : 400}
                  color={currentParams.checkOut ? 'text.primary' : 'text.secondary'}
                >
                  {currentParams.checkOut ? format(new Date(currentParams.checkOut), 'MMM d') : 'Add dates'}
                </Typography>
              </Box>

              {/* Who */}
              <Box
                sx={{
                  flex: 0.8,
                  px: getSearchBarPadding(),
                  py: 1,
                }}
              >
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Who
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={currentParams.guests ? 500 : 400}
                  color={currentParams.guests ? 'text.primary' : 'text.secondary'}
                >
                  {getGuestText()}
                </Typography>
              </Box>

              {/* Search Button */}
              <Box sx={{ px: 1 }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    minWidth: 48,
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    p: 0,
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  <SearchIcon />
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        <SearchModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onSearch={handleSearch}
          initialParams={currentParams}
        />
      </>
    );
  }

  // Compact variant (smaller, for navigation or secondary placement)
  return (
    <>
      <TextField
        placeholder={placeholder}
        variant="outlined"
        size={size === 'large' ? 'medium' : size}
        onClick={handleOpenModal}
        value={hasSearchParams ? `${getLocationText()} • ${getDateText()} • ${getGuestText()}` : ''}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          sx: {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            backgroundColor: 'background.paper',
          },
          '& .MuiInputBase-input': {
            cursor: 'pointer',
          },
        }}
      />

      <SearchModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSearch={handleSearch}
        initialParams={currentParams}
      />
    </>
  );
};

export default SearchBar;